/**
 * Request deduplication utility to prevent duplicate API calls
 * Useful for preventing race conditions in React components
 */
class RequestDeduplicator {
    constructor() {
        this.pendingRequests = new Map();
    }

    /**
     * Execute a request, deduplicating based on the request key
     * @param {string} key - Unique key for the request
     * @param {Function} requestFn - Function that returns a Promise
     * @returns {Promise} - The request promise
     */
    async execute(key, requestFn) {
        // If request is already pending, return the existing promise
        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key);
        }

        // Create new request promise
        const requestPromise = requestFn()
            .finally(() => {
                // Clean up after request completes
                this.pendingRequests.delete(key);
            });

        // Store the promise
        this.pendingRequests.set(key, requestPromise);

        return requestPromise;
    }

    /**
     * Clear all pending requests
     */
    clear() {
        this.pendingRequests.clear();
    }

    /**
     * Clear specific request by key
     * @param {string} key - Request key to clear
     */
    clearRequest(key) {
        this.pendingRequests.delete(key);
    }

    /**
     * Check if a request is pending
     * @param {string} key - Request key to check
     * @returns {boolean} - True if request is pending
     */
    isPending(key) {
        return this.pendingRequests.has(key);
    }

    /**
     * Get count of pending requests
     * @returns {number} - Number of pending requests
     */
    getPendingCount() {
        return this.pendingRequests.size;
    }
}

// Create a global instance for API requests
export const apiDeduplicator = new RequestDeduplicator();

export default RequestDeduplicator;